import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Chip } from "fr-ito-web-react/react/esm";
import { CombinedReducersType } from "@redux/reducer";
import { CountryEnv, LANGUAGE, MemberPrices } from "@type/index";
import { extractValidPrice } from "@util/currency";
import { formatPrice } from "@util/helpers";
import { formatDate } from "@util/date";
import { SB_ENV } from "@config/common";
import styles from "./styles.module.css";

type Props = {
  prices?: MemberPrices;
  isLoggedIn: boolean;
};

const PRICE_PLACEHOLDER = "__MEMBER_PRICE__";

export const MemberPricePromotionPanel = ({ prices, isLoggedIn }: Props) => {
  const { t } = useTranslation();
  const { brand_name, region, language } = useSelector(
    (store: CombinedReducersType) => store.config
  );
  const promoInfo = prices?.promoInfo;
  const promotionType = promoInfo?.base;

  if (!promotionType || isLoggedIn) return null;

  const onlineLimited = promoInfo?.onlineLimited ?? false;

  if (promotionType === "multibuy") {
    return (
      <Chip
        size="small"
        clickable={false}
        className={styles.taglinePromotionPrimary}
      >
        {t(
          onlineLimited
            ? "text_membership_exclusive_multibuy_logged_out"
            : "text_membership_exclusive_multibuy_logged_out_app"
        )}
      </Chip>
    );
  }

  const countryConfig: CountryEnv = SB_ENV(brand_name, region);
  const memberPromoEndDate = promoInfo?.endDate
    ? formatDate(
        promoInfo.endDate,
        language as LANGUAGE,
        countryConfig,
        undefined,
        true
      )
    : "";
  const memberPromoStartDate = promoInfo?.startDate
    ? formatDate(
        promoInfo.startDate,
        language as LANGUAGE,
        countryConfig,
        undefined,
        true
      )
    : "";

  const validPrice = prices && extractValidPrice(prices);
  const memberPrice =
    validPrice && validPrice.value > 0 ? formatPrice(validPrice.value) : "";

  if (!memberPrice) return null;

  const formattedMemberPrice = t("currency_format", {
    price: memberPrice
  });
  const promoText = t(
    onlineLimited
      ? "text_membership_exclusive_price_logged_out"
      : "text_membership_exclusive_price_logged_out_app",
    {
      price: PRICE_PLACEHOLDER,
      date: memberPromoEndDate || "",
      startDate: memberPromoStartDate || ""
    }
  );
  const [pricePrefix, priceSuffix] = promoText.split(PRICE_PLACEHOLDER);

  return (
    <Chip
      size="small"
      clickable={false}
      className={styles.taglinePromotionPrimary}
    >
      {priceSuffix === undefined ? (
        promoText
      ) : (
        <span>
          {pricePrefix}
          <strong className={styles.memberPrice}>{formattedMemberPrice}</strong>
          {priceSuffix}
        </span>
      )}
    </Chip>
  );
};
